import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, Outlet, ScrollRestoration, useLocation } from 'react-router'

import { EmbedRoot } from '@/embed/child/EmbedRoot'
import shareImage from '../assets/share-image.png'
import { isProduction, redirectsMarketingToApp } from '../config/config'
import { INSURANCE_VERTICAL_PATHS, isInsuranceVerticalPath } from '../config/insuranceVerticals'
import { decorateCrossDomainLinks } from '../lib/crossDomainTracking'
import { getCsrfCookieToken } from '../lib/csrfCookie'
import { useFollowAccountChanges } from '../models/auth'
import { insureQueryClient as queryClient } from '../models/insureQueryClient'

// The CSRF cookie when Insure's frame last mounted. Django rotates it on every sign-in, and the shared shell
// brings the browser back to Insure without a page load, perhaps as another account: then the cache is theirs.
let mountedCsrf = ''

/** The layout every Insure page renders in: its API client, page metadata and scroll handling. */
export function InsureFrame() {
  useEffect(() => decorateCrossDomainLinks(), [])
  // Lazily, so it runs before any page below reads the cache.
  useState(() => {
    const csrf = getCsrfCookieToken()
    if (mountedCsrf && mountedCsrf !== csrf) queryClient.clear()
    mountedCsrf = csrf
  })

  return (
    <QueryClientProvider client={queryClient}>
      <InsurePage />
    </QueryClientProvider>
  )
}

function InsurePage() {
  useFollowAccountChanges()
  const location = useLocation()
  // Pages resolve with or without a trailing slash, so metadata checks must too.
  const pagePath = location.pathname.replace(/(.)\/+$/, '$1')
  const hasOwnDescription = [
    '/',
    '/insurance',
    '/group-captive',
    '/how-we-use-ai',
    '/privacy',
    '/terms',
    '/fuelapp',
    '/fuelapp/referrals',
    '/fuelapp/teams',
    '/bookkeeper',
    '/cfo',
    '/insurance/large-fleet-coverage',
    '/insurance/amazon-relay',
    ...INSURANCE_VERTICAL_PATHS,
  ].includes(pagePath)

  const hasOwnSharePreview = isInsuranceVerticalPath(location.pathname)

  if (redirectsMarketingToApp(location.pathname)) return <Navigate to={`/insure${location.search}`} replace />

  return (
    <>
      <Helmet titleTemplate="Cai | %s" defaultTitle="Cai"></Helmet>
      <meta name="title" content="Cai" />
      {!hasOwnDescription && (
        <meta
          name="description"
          content="Cai Labs makes AI-powered financial technology for the businesses that build, move, and fix the world around us."
        />
      )}
      {!hasOwnSharePreview && (
        <>
          <meta name="og:image" content={shareImage} />
          <meta
            name="og:title"
            content="Cai Labs makes AI-powered financial technology for the businesses that build, move, and fix the world around us."
          />
        </>
      )}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      {!isProduction && <meta name="robots" content="noindex, nofollow" />}
      <Outlet />
      <ScrollRestoration />
      <EmbedRoot />
    </>
  )
}
